import BaseStore from '@/store/BaseStore'
import { Action, Module, Mutation } from '@/node_modules/vuex-module-decorators'
import {
  defaultGetUserOMemosAndNotesByIdRequest,
  defaultLendOutRequest,
  defaultUserOfMemosAndNotes,
  DueDate,
  GetUserOfMemosAndNotesByIdRequest,
  lendApi,
  LendCollection,
  LendExtend,
  LendOutRequest,
  NowStatus,
  ReceiveWaitingCollection,
  ReserveCancelCollection,
  ReserveCollection,
  userApi,
  UserMemo,
  UserNote,
  UserOfMemosAndNotes,
  defaultLendUserInfo,
  LendUserInfo,
  UserBase,
  UserFamily,
  UserPersonal,
  RemindHistory,
  LendCollectionInfo,
  LendCollectionList,
  LendUserMessage,
  LendOutReceiptInfo,
  defaultLendReceiptInfo
} from '@/api'
import { UserInfoTableItem } from '@/components/organisms/UserInfoTable/UserInfoTable.type'
import { LendUserUsageSituationItem } from '@/components/organisms/LendNavigationUserUsageSituation/index.type'
import {
  defaultLendNavigationSettingItem,
  LendNavigationSettingItem
} from '@/components/organisms/LendNavigationSetting/index.type'
import { LendListLendCollectionTableItem } from '@/components/templates/lend/LendListLendCollectionTable.vue'
import {
  dataStore,
  lendStore,
  systemParameterStore
} from '@/utils/store-accessor'
import { isEqual, startsWith } from 'lodash'
import {
  CollectionStatus,
  defaultReceiptPrintReceiptCollectionStatusConfirmData
} from '@/types'

@Module({
  name: 'LendStore',
  namespaced: true,
  stateFactory: true
})
export default class LendStore extends BaseStore {
  // 恐らく、storeで管理する必要が無い値もあると思う
  // 時間があれば整理したい

  // 今回の貸出資料検索で取得した資料ID
  searchLendCollectionId: number = 0

  // 今回の貸出資料で入力した資料番号
  inputtedSearchLendCollectionNo: string = ''

  // 貸出中の資料で入力した資料番号
  inputtedSearchLendOutCollectionNo: string = ''

  // 貸出中の資料で選択した資料ID
  checkedLendOutCollectionIds: number[] = []

  // 利用者情報テーブルの入力欄
  inputtedSearchUserId: string = ''

  // 今回の貸出資料リスト
  lendCollectionList: LendListLendCollectionTableItem[] = []

  // 今回の貸出資料タブに表示する返却予定日
  lendCollectionListDueDates: DueDate[] = []

  // 貸出延長資料
  lendExtendCollections: LendExtend[] = []

  // 画面右部のナビゲーションバー情報
  lendNavigationSettingItem: LendNavigationSettingItem = defaultLendNavigationSettingItem()

  // 貸出リクエスト
  lendOutRequest: LendOutRequest = defaultLendOutRequest()

  // 貸出利用者情報
  lendUserInfo: LendUserInfo = defaultLendUserInfo()

  // 削除する督促履歴ID
  remindHistoriesDeleteId: number = -1

  // 連絡メモ・注記
  userOfMemosAndNotes: UserOfMemosAndNotes = defaultUserOfMemosAndNotes()

  // ダイアログ「連絡メモ・注記」に表示する資料メモ取得リクエスト
  userOfMemosAndNotesGetRequest: GetUserOfMemosAndNotesByIdRequest = defaultGetUserOMemosAndNotesByIdRequest()

  receiptData: LendOutReceiptInfo = defaultLendReceiptInfo()

  // 館内閲覧か否か
  _isReadingInside: boolean = false

  _isReadingMyNumberCard: boolean = false

  // 貸出利用者を読込後、一回でも読み込みをしたかどうか
  _bulkLending: boolean = false

  get getSearchLendCollectionId(): number {
    return this.searchLendCollectionId
  }

  get getInputtedSearchLendCollectionNo(): string {
    return this.inputtedSearchLendCollectionNo
  }

  get getInputtedSearchLendOutCollectionNo(): string {
    return this.inputtedSearchLendOutCollectionNo
  }

  get getCheckedLendOutCollectionIds(): number[] {
    return this.checkedLendOutCollectionIds
  }

  get getInputtedSearchUserId(): string {
    return this.inputtedSearchUserId
  }

  get getLendCollectionList(): LendListLendCollectionTableItem[] {
    return this.lendCollectionList
  }

  get displayLendCollectionList(): LendListLendCollectionTableItem[] {
    return this.getLendCollectionList.filter(
      value => value.collectionStatus.code !== CollectionStatus.Composed
    )
  }

  get lendListLendCollectionTableItems(): LendListLendCollectionTableItem[] {
    return [
      ...this.getTodaysLendCollections.map(collection => ({
        uniqueId: collection.id + 'lendOut',
        isWriteAfiTarget: false,
        isLendOut: true,
        collectionId: collection.id,
        collectionNo: collection.collectionNo,
        parentComposedCollectionId: 0, // この値で親資料番号は使用しない
        bibliographyId: collection.bibliographyId,
        bibliographyClass: collection.bibliographyClass,
        prefix: collection.prefix,
        searchType: collection.searchType,
        displayTitle: collection.displayTitle,
        displayAuthor: collection.displayAuthor,
        appendixCode: collection.appendixCode,
        collectionType: collection.collectionType,
        handlingType: collection.handlingType,
        collectionStatus: collection.collectionStatus,
        workMemo: collection.workMemo,
        displayMemo: collection.displayMemo,
        localMemo: collection.localMemo,
        dueDate: collection.dueDate,
        hasReservation: collection.hasReservation,
        hasReserveForwarding: false,
        collectionFlag: collection.collectionFlag,
        appendixLendFlag: collection.appendixLendFlag,
        lendUserId: collection.lendUserId,
        lendClassification: collection.lendClassification,
        _deleteHandlingType: false,
        shelfType: collection.shelfType,
        revision: collection.revision,
        messages: [],
        branch: collection.branch,
        lendUserReserveWaitingId: 0,
        lendUserReserveWaitingRevision: 0,
        receiptCollectionStatusInfo: defaultReceiptPrintReceiptCollectionStatusConfirmData()
      })),
      ...this.displayLendCollectionList
    ]
  }

  get getTodaysLendCollections(): LendCollection[] {
    return this.lendUserInfo.user.todaysLendCollections
  }

  get getLendCollectionListDueDates(): DueDate[] {
    return this.lendCollectionListDueDates
  }

  get getLendNavigationSettingItem(): LendNavigationSettingItem {
    return this.lendNavigationSettingItem
  }

  get getLendOutRequest(): LendOutRequest {
    return this.lendOutRequest
  }

  get getLendUserBase(): UserBase {
    return this.lendUserInfo.user.base
  }

  get getLendUserDueDates(): DueDate[] {
    return this.lendUserInfo.dueDate
  }

  get getLendUserFamilyUsers(): UserFamily[] {
    return this.lendUserInfo.user.familyUsers
  }

  get getLendUserInfo(): LendUserInfo {
    return this.lendUserInfo
  }

  get getLendUserLendCollections(): LendCollection[] {
    return this.lendUserInfo.user.lendCollections
  }

  get getLendUserMemos(): UserMemo[] {
    return this.lendUserInfo.user.memos
  }

  get getLendUserMessages(): LendUserMessage[] {
    return this.lendUserInfo.messages
  }

  get getLendUserNotes(): UserNote[] {
    return this.lendUserInfo.user.notes
  }

  get getLendUserNowStatus(): NowStatus {
    return this.lendUserInfo.user.nowStatus
  }

  get getLendUserPersonal(): UserPersonal {
    return this.lendUserInfo.user.personal
  }

  get getLendUserReceiveWaitingCollections(): ReceiveWaitingCollection[] {
    return this.lendUserInfo.receiveWaitingCollections
  }

  get getLendUserReserveCancelCollections(): ReserveCancelCollection[] {
    return this.lendUserInfo.user.reserveCancelCollections
  }

  get getLendUserRemindHistories(): RemindHistory[] {
    return this.lendUserInfo.user.remindHistories
  }

  get getLendUserReserveCollections(): ReserveCollection[] {
    return this.lendUserInfo.user.reserveCollections
  }

  get getUserOfMemosAndNotesGetRequest(): GetUserOfMemosAndNotesByIdRequest {
    return this.userOfMemosAndNotesGetRequest
  }

  get getUserInfoTableItem(): UserInfoTableItem {
    return {
      id: this.getInputtedSearchUserId,
      userType: this.getLendUserBase.userType,
      name: this.getLendUserPersonal.name ?? '',
      workType: this.getLendUserPersonal.workType,
      gender: this.getLendUserPersonal.gender,
      expirationDate: this.getLendUserBase.expirationDate ?? '',
      nameKana: this.getLendUserPersonal.nameKana ?? '',
      status: this.getLendUserBase.status,
      phoneNumber: this.getLendUserPersonal.phones[0]?.number ?? '',
      address: this.getLendUserPersonal.userAddress.address ?? '',
      studentGrade: this.getLendUserPersonal.studentGrade,
      studentClass: this.getLendUserPersonal.studentClass,
      studentNumber: this.getLendUserPersonal.studentNumber
    }
  }

  get getUserOfMemosAndNotes(): UserOfMemosAndNotes {
    return this.userOfMemosAndNotes
  }

  get getUserUsageSituationItem(): LendUserUsageSituationItem {
    return {
      lendCountList: this.getLendUserNowStatus.lendCountList,
      lendLatestCount: this.getLendUserNowStatus.lendLatestCount ?? 0,
      lendLatestLimit: this.getLendUserNowStatus.lendLatestLimit ?? 0,
      lendTotalCount: this.getLendUserNowStatus.lendTotalCount ?? 0,
      lendTotalLimit: this.getLendUserNowStatus.lendTotalLimit ?? 0,
      insideReadingCount: this.getLendUserNowStatus.insideReadingCount ?? 0,
      lostCount: this.getLendUserNowStatus.lostCount ?? 0,
      reserveCount: this.getLendUserNowStatus.reserveCount ?? 0,
      overdueCount: this.getLendUserNowStatus.overdueCount ?? 0
    }
  }

  get getReceiptData(): LendOutReceiptInfo {
    return this.receiptData
  }

  get isReadingInside(): boolean {
    return this._isReadingInside
  }

  get existsLendUserInfo(): boolean {
    return !isEqual(this.lendUserInfo, defaultLendUserInfo())
  }

  get isGroupUser(): boolean {
    return startsWith(this.lendUserInfo.user.base.userType.code, '8')
  }

  get displayOutputReceipt(): boolean {
    if (this.existsLendUserInfo) {
      return (
        this.lendUserInfo.displayReceiptOutput === '1' ||
        this.lendUserInfo.displayReceiptOutput === '2'
      )
    }

    if (this.isReadingInside) {
      return dataStore.displayReceiptOutputInsideReading
    }

    return true
  }

  get displayShortReceipt(): boolean {
    if (!this.existsLendUserInfo) {
      return false
    }

    if (this.isReadingInside) {
      return systemParameterStore.displayLendShortReceiptByReadingInside
    } else if (this.isGroupUser) {
      return systemParameterStore.displayLendShortReceiptByGroupUser
    } else {
      return systemParameterStore.displayLendShortReceipt
    }
  }

  get defaultShortReceipt(): boolean {
    if (!this.existsLendUserInfo) {
      return false
    }

    if (this.isReadingInside) {
      return systemParameterStore.defaultCheckLendShortReceiptByReadingInside
    } else if (this.isGroupUser) {
      return systemParameterStore.defaultCheckLendShortReceiptByGroupUser
    } else {
      return systemParameterStore.defaultCheckLendShortReceipt
    }
  }

  get isReadingMyNumberCard(): boolean {
    return this._isReadingMyNumberCard
  }

  get bulkLending(): boolean {
    return this._bulkLending
  }

  @Mutation
  public initialize() {
    this.searchLendCollectionId = 0
    this.inputtedSearchLendCollectionNo = ''
    this.inputtedSearchLendOutCollectionNo = ''
    this.checkedLendOutCollectionIds = []
    this.inputtedSearchUserId = ''
    this.lendCollectionList = []
    this.lendCollectionListDueDates = []
    this.lendExtendCollections = []
    this.lendNavigationSettingItem = defaultLendNavigationSettingItem()
    this.lendOutRequest = defaultLendOutRequest()
    this.lendUserInfo = defaultLendUserInfo()
    this.remindHistoriesDeleteId = -1
    this.userOfMemosAndNotes = defaultUserOfMemosAndNotes()
    this.userOfMemosAndNotesGetRequest = defaultGetUserOMemosAndNotesByIdRequest()
    this.receiptData = defaultLendReceiptInfo()
    this._isReadingMyNumberCard = false
    this._bulkLending = false

    if (lendStore.isReadingInside) {
      this.lendNavigationSettingItem.receiptEnabled =
        dataStore.lendNavigationReceiptOutputDefaultValueOnInsideReading
    } else {
      this.lendNavigationSettingItem.receiptEnabled = true
    }
  }

  // 連続貸出で利用する。こちらは敢えて利用者情報を削除する意味がない為
  @Mutation
  public initializeNonSearchUserInfo(): void {
    this.searchLendCollectionId = 0
    this.inputtedSearchLendCollectionNo = ''
    this.inputtedSearchLendOutCollectionNo = ''
    this.checkedLendOutCollectionIds = []
    // this.inputtedSearchUserId = '' // initialize()とどのような違いがあるのか分かりづらいのでコメントアウトで残しておく
    this.lendCollectionList = []
    this.lendCollectionListDueDates = []
    this.lendExtendCollections = []
    this.lendNavigationSettingItem = defaultLendNavigationSettingItem()
    this.lendOutRequest = defaultLendOutRequest()
    // this.lendUserInfo = defaultLendUserInfo() // initialize()とどのような違いがあるのか分かりづらいのでコメントアウトで残しておく
    this.remindHistoriesDeleteId = -1
    this.userOfMemosAndNotes = defaultUserOfMemosAndNotes()
    this.userOfMemosAndNotesGetRequest = defaultGetUserOMemosAndNotesByIdRequest()
    this.receiptData = defaultLendReceiptInfo()
    // this._isReadingMyNumberCard = false // initialize()とどのような違いがあるのか分かりづらいのでコメントアウトで残しておく

    this.lendNavigationSettingItem.receiptEnabled =
      lendStore.lendUserInfo.displayReceiptOutput === '1'
  }

  @Mutation
  public clearInputtedSearchUserId() {
    this.inputtedSearchUserId = ''
  }

  @Mutation
  public pushLendCollectionList(obj: LendListLendCollectionTableItem) {
    this.lendCollectionList.push(obj)
  }

  @Mutation
  public setRemindHistoriesDeleteId(value: number) {
    this.remindHistoriesDeleteId = value
  }

  @Mutation
  public setSearchLendCollectionId(id: number) {
    this.searchLendCollectionId = id
  }

  @Mutation
  public setInputtedSearchLendCollectionNo(no: string) {
    this.inputtedSearchLendCollectionNo = no
  }

  @Mutation
  public setInputtedSearchLendOutCollectionNo(no: string) {
    this.inputtedSearchLendOutCollectionNo = no
  }

  @Mutation
  public setCheckedLendOutCollectionIds(array: number[]) {
    this.checkedLendOutCollectionIds = array
  }

  @Mutation
  public pushCheckedLendOutCollectionIds(...value: number[]) {
    this.checkedLendOutCollectionIds.push(...value)
  }

  @Mutation
  public spliceCheckedLendOutCollectionIds(obj: {
    index: number
    deleteCount?: number
  }) {
    this.checkedLendOutCollectionIds.splice(obj.index, obj.deleteCount)
  }

  @Mutation
  public setInputtedSearchUserId(id: string) {
    this.inputtedSearchUserId = id
  }

  @Mutation
  public deleteLendCollectionList(obj: {
    index: number
    deleteCount?: number
  }) {
    const parentId: number = this.lendCollectionList[obj.index].collectionId
    this.lendCollectionList.splice(obj.index, obj.deleteCount)
    this.lendCollectionList = this.lendCollectionList.filter(
      value => value.parentComposedCollectionId !== parentId
    )
  }

  @Mutation
  public updateLendListLendCollectionTableItems(
    lendCollection: LendCollectionList
  ) {
    let index = this.lendCollectionList.findIndex(
      value => value.collectionId === lendCollection.collectionId
    )

    if (~index) {
      this.lendCollectionList[index] = {
        uniqueId: this.lendCollectionList[index].uniqueId,
        isWriteAfiTarget: this.lendCollectionList[index].isWriteAfiTarget,
        isLendOut: this.lendCollectionList[index].isLendOut,
        ...lendCollection
      }

      this.lendCollectionList = [...this.lendCollectionList]
    }

    index = this.lendUserInfo.user.todaysLendCollections.findIndex(
      value => value.id === lendCollection.collectionId
    )

    if (~index) {
      this.lendUserInfo.user.todaysLendCollections[index] = {
        id: lendCollection.collectionId, // 資料ID
        collectionNo: lendCollection.collectionNo,
        bibliographyId: lendCollection.bibliographyId,
        bibliographyClass: lendCollection.bibliographyClass,
        prefix: lendCollection.prefix,
        searchType: lendCollection.searchType,
        displayTitle: lendCollection.displayTitle,
        displayAuthor: lendCollection.displayAuthor,
        appendixCode: lendCollection.appendixCode,
        lendTime: this.lendUserInfo.user.todaysLendCollections[index].lendTime,
        lendType: this.lendUserInfo.user.todaysLendCollections[index].lendType,
        collectionType: lendCollection.collectionType,
        shelfType: lendCollection.shelfType,
        ndc: this.lendUserInfo.user.todaysLendCollections[index].ndc,
        branch: this.lendUserInfo.user.todaysLendCollections[index].branch,
        reserveCount: this.lendUserInfo.user.todaysLendCollections[index]
          .reserveCount,
        dueDate: this.lendUserInfo.user.todaysLendCollections[index].dueDate,
        extendCount: this.lendUserInfo.user.todaysLendCollections[index]
          .extendCount,
        remindCount: this.lendUserInfo.user.todaysLendCollections[index]
          .remindCount,
        message: this.lendUserInfo.user.todaysLendCollections[index].message,
        collectionFlag: lendCollection.collectionFlag,
        appendixLendFlag: lendCollection.appendixLendFlag,
        revision: lendCollection.revision,
        handlingType: lendCollection.handlingType,
        collectionStatus: lendCollection.collectionStatus,
        workMemo: lendCollection.workMemo,
        displayMemo: lendCollection.displayMemo,
        localMemo: lendCollection.localMemo,
        hasReservation: lendCollection.hasReservation,
        hasReserveForwarding: lendCollection.hasReserveForwarding,
        lendUserId: lendCollection.lendUserId,
        lendClassification: lendCollection.lendClassification
      }

      this.lendUserInfo.user.todaysLendCollections = [
        ...this.lendUserInfo.user.todaysLendCollections
      ]
    }
  }

  @Mutation
  public setLendCollectionListDueDates(array: DueDate[]) {
    this.lendCollectionListDueDates = array
  }

  @Mutation
  public setLendExtendCollections(array: LendExtend[]) {
    this.lendExtendCollections = array
  }

  @Mutation
  public setLendNavigationSettingItemDueDate(value: string) {
    this.lendNavigationSettingItem.dueDate = value
  }

  @Mutation
  public setLendNavigationSettingItemHandlingType(value: string) {
    this.lendNavigationSettingItem.handlingType = value
  }

  @Mutation
  public setLendNavigationSettingItemReceiptEnabled(value: boolean) {
    this.lendNavigationSettingItem.receiptEnabled = value
  }

  @Mutation
  public initializeLendNavigationSettingItemReceiptEnabled(): void {
    if (lendStore.existsLendUserInfo) {
      if (
        lendStore.isReadingInside &&
        lendStore.lendUserInfo.displayReceiptOutput === '0'
      ) {
        this.lendNavigationSettingItem.receiptEnabled = false
      } else {
        this.lendNavigationSettingItem.receiptEnabled =
          lendStore.lendUserInfo.displayReceiptOutput === '1'
      }

      return
    }

    if (lendStore.isReadingInside) {
      if (dataStore.displayReceiptOutputInsideReading) {
        this.lendNavigationSettingItem.receiptEnabled = false
      } else {
        this.lendNavigationSettingItem.receiptEnabled =
          dataStore.lendNavigationReceiptOutputDefaultValueOnInsideReading
      }

      return
    }

    this.lendNavigationSettingItem.receiptEnabled = true
  }

  @Mutation
  public setLendNavigationSettingItemIsAbbreviationReceiptInfo(value: boolean) {
    this.lendNavigationSettingItem.isAbbreviationReceiptInfo = value
  }

  @Mutation
  public setLendNavigationSettingItemShelfType(value: string) {
    this.lendNavigationSettingItem.shelfType = value
  }

  @Mutation
  public setLendNavigationSettingItemDeleteHandlingType(value: boolean) {
    this.lendNavigationSettingItem._deleteHandlingType = value
  }

  @Mutation
  public setLendOutRequest(value: LendOutRequest) {
    this.lendOutRequest = value
  }

  @Mutation
  public setLendUserInfo(item: LendUserInfo) {
    this.lendUserInfo = item
  }

  @Mutation
  public setLendUserLendCollections(value: LendCollection[]) {
    this.lendUserInfo.user.lendCollections = value
  }

  @Mutation
  public setLendUserReserveCollections(value: ReserveCollection[]) {
    this.lendUserInfo.user.reserveCollections = value
  }

  @Mutation
  public setLendUserMemos(memos: UserMemo[]) {
    this.lendUserInfo.user.memos = memos
  }

  @Mutation
  public setLendUserNotes(notes: UserNote[]) {
    this.lendUserInfo.user.notes = notes
  }

  @Mutation
  public setLendUserNowStatus(nowStatus: NowStatus) {
    this.lendUserInfo.user.nowStatus = nowStatus
  }

  @Mutation
  public setLendUserRemindHistories(remindHistories: RemindHistory[]) {
    this.lendUserInfo.user.remindHistories = remindHistories
  }

  @Mutation
  public setLendUserReserveCancelCollections(
    collections: ReserveCancelCollection[]
  ) {
    this.lendUserInfo.user.reserveCancelCollections = collections
  }

  @Mutation
  public setUserOfMemosAndNotes(item: UserOfMemosAndNotes) {
    this.userOfMemosAndNotes = item
  }

  @Mutation
  public setUserOfMemosAndNotesGetRequest(
    item: GetUserOfMemosAndNotesByIdRequest
  ) {
    this.userOfMemosAndNotesGetRequest = item
  }

  @Mutation
  public setReceiptData(data: LendOutReceiptInfo) {
    this.receiptData = data
  }

  @Mutation
  public setIsReadingInside(value: boolean) {
    this._isReadingInside = value
  }

  @Mutation
  public setIsReadingMyNumberCard(value: boolean) {
    this._isReadingMyNumberCard = value
  }

  @Mutation
  public setBulkLending(value: boolean) {
    this._bulkLending = value
  }

  @Action({ rawError: true })
  public async fetchLendCollectionListDueDates() {
    const items: DueDate[] = await lendApi.getDueDates()
    this.setLendCollectionListDueDates(items)
  }

  @Action({ rawError: true })
  public async fetchLendExtendCollections() {
    const items: LendExtend[] = await lendApi.getLendExtend({
      id: this.getLendUserBase.id
    })
    this.setLendExtendCollections(items)
  }

  @Action({ rawError: true })
  public async fetchUserOfMemosAndNotesById() {
    const item: UserOfMemosAndNotes = await userApi.getUserOfMemosAndNotesById(
      this.getUserOfMemosAndNotesGetRequest
    )
    this.setUserOfMemosAndNotes(item)
  }

  @Action({ rawError: true })
  public async fetchUserLendCollectionsById() {
    const items: LendCollectionInfo = await userApi.getUserLendCollectionsById(
      this.getLendUserBase.id
    )
    this.setLendUserLendCollections(items.collections)
  }

  @Action({ rawError: true })
  public async fetchUserNowStatusById() {
    const item: NowStatus = await userApi.getUserNowStatusById({
      id: this.getLendUserBase.id
    })
    this.setLendUserNowStatus(item)
  }

  @Action({ rawError: true })
  public async fetchUserRemindHistoriesById() {
    const items: RemindHistory[] = await userApi.getUserRemindHistoriesById({
      id: this.getLendUserBase.id
    })
    this.setLendUserRemindHistories(items)
  }

  @Action({ rawError: true })
  public async fetchUserReserveCancelCollectionsById() {
    const items: ReserveCancelCollection[] = await userApi.getUserReserveCancelCollectionsById(
      {
        id: this.getLendUserBase.id
      }
    )

    this.setLendUserReserveCancelCollections(items)
  }

  @Action({ rawError: true })
  public async fetchUserReserveCollections() {
    const items: ReserveCollection[] = await userApi.getUserReserveCollections({
      id: this.getLendUserBase.id
    })

    this.setLendUserReserveCollections(items)
  }

  @Action({ rawError: true })
  public async lendOut() {
    const receiptData: LendOutReceiptInfo = await lendApi.lendOut(
      this.getLendOutRequest
    )
    this.setReceiptData(receiptData)
  }
}
